import React, { useMemo, useState } from 'react'
import { Table, Button, Alert, Input, FormGroup, ButtonGroup } from 'reactstrap'
import { useQuery } from '@apollo/client'
import { GET_ADMIN_TOPICS } from './Queries'
import CreateEditTopicModal from './CreateEditTopicModal'

export default function TopicManagement() {
    const [showModal, setShowModal] = useState(false)
    const [selectedTopic, setSelectedTopic] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [feedback, setFeedback] = useState(null)

    const { data, loading, error, refetch } = useQuery(GET_ADMIN_TOPICS, {
        variables: { first: 100 },
        fetchPolicy: 'network-only'
    })

    const topics = useMemo(
        () => data?.allTopics?.edges?.map(edge => edge.node) || [],
        [data]
    )

    const filteredTopics = useMemo(() => {
        const lower = searchTerm.trim().toLowerCase()
        if (!lower) return topics
        return topics.filter(topic => `${topic.name} ${topic.description || ''}`.toLowerCase().includes(lower))
    }, [topics, searchTerm])

    const handleCreate = () => {
        setSelectedTopic(null)
        setShowModal(true)
    }

    const handleEdit = (topic) => {
        setSelectedTopic(topic)
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        setSelectedTopic(null)
    }

    const handleModalSuccess = (message) => {
        refetch()
        setFeedback({ type: 'success', message })
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        )
    }

    if (error) {
        return <Alert color="danger">Error loading topics: {error.message}</Alert>
    }

    return (
        <div className="comms-admin-section">
            <div className="comms-admin-section-header">
                <div>
                    <h3>Topics</h3>
                    <p className="text-muted">Manage topic names and descriptions. Descriptions help the AI pick the right topic for new messages.</p>
                </div>
                <div>
                    <Button color="primary" onClick={handleCreate}>
                        <i className="fa fa-plus"></i> Create Topic
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert color={feedback.type}>
                    <i className="fa fa-close" onClick={() => setFeedback(null)}></i> {feedback.message}
                </Alert>
            )}

            <div className="comms-admin-filters mb-3">
                <FormGroup className="mb-0 flex-grow-1">
                    <Input
                        type="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search topics by name or description…"
                    />
                </FormGroup>
            </div>

            {filteredTopics.length === 0 ? (
                <Alert color="info">
                    {topics.length === 0 ? 'No topics yet. Create your first topic to help route and classify conversations.' : 'No topics match your search.'}
                </Alert>
            ) : (
                <Table responsive striped hover className="comms-admin-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th style={{ width: '120px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTopics.map((topic) => (
                            <tr key={topic.id}>
                                <td><strong>{topic.name}</strong></td>
                                <td>{topic.description || <span className="text-muted">—</span>}</td>
                                <td>
                                    <ButtonGroup size="sm">
                                        <Button color="info" outline onClick={() => handleEdit(topic)}>
                                            <i className="fa fa-edit"></i> Edit
                                        </Button>
                                    </ButtonGroup>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {showModal && (
                <CreateEditTopicModal
                    isOpen={showModal}
                    toggle={closeModal}
                    topic={selectedTopic}
                    onSuccess={handleModalSuccess}
                />
            )}
        </div>
    )
}
